/* eslint-disable sort-keys-fix/sort-keys-fix */
import { useConfiguration } from '@arvesta-websites/configuration';

import { ContentfulImage } from '../../../__generated__/graphql-types';
import * as CommonUtils from '../../utils';

type propsType = {
  metaData: {
    canonical: string;
    description: string;
    image: string;
    nofollow: boolean;
    noindex: boolean;
    siteName: string;
    title: string;
    updatedAt: string;
    pageLocale: string;
    pageType: string;
    searchTags: string;
    swiftTypeDate: string;
    swiftTypeLocation: string;
    ogPageType: string;
  };
  image: ContentfulImage;
};

const BaseHead = ({ metaData, image }: propsType): JSX.Element => {
  const config = useConfiguration();

  const metaLinks = [];
  const metaTags = [];

  if (metaData.canonical) {
    metaLinks.push({
      href: metaData.canonical,
      rel: 'canonical',
    });
  }

  // // Open Graph specs
  metaTags.push({
    property: 'og:site_name',
    content: metaData.siteName ? metaData.siteName : '',
  });

  // // Essential Meta Tags
  metaTags.push({
    property: 'og:title',
    content: metaData.title || metaData.siteName,
  });
  if (metaData.canonical) {
    metaTags.push({
      property: 'og:url',
      content: metaData.canonical,
    });
  }
  if (metaData.description) {
    metaTags.push({
      name: 'description',
      content: metaData.description,
    });
    metaTags.push({
      property: 'og:description',
      content: metaData.description,
    });
  }
  if (metaData.image) {
    metaTags.push({
      property: 'og:image',
      content: metaData.image,
    });
  }

  // // Twitter specific
  metaTags.push({
    name: 'twitter:card',
    content: 'summary_large_image',
  });
  // // metaTags.push({
  // //   name: 'twitter:card',
  // //   content: 'summary',
  // // });
  metaTags.push({
    name: 'twitter:title',
    content: metaData.title || metaData.siteName,
  });
  if (metaData.description) {
    metaTags.push({
      name: 'twitter:description',
      content: metaData.description,
    });
  }
  if (metaData.image) {
    metaTags.push({
      name: 'twitter:image',
      content: metaData.image,
    });
  }
  metaTags.push({
    property: 'og:locale',
    content: metaData.pageLocale,
  });

  // // LinkedIn optimizations
  metaTags.push({
    property: 'og:type',
    content: metaData.ogPageType,
  });

  // // Handle nofollow and noindex
  if (metaData.nofollow && metaData.noindex) {
    metaTags.push({
      name: 'robots',
      content: 'noindex, nofollow',
    });
  } else if (metaData.nofollow) {
    metaTags.push({
      name: 'robots',
      content: 'nofollow',
    });
  } else if (metaData.noindex) {
    metaTags.push({
      name: 'robots',
      content: 'noindex',
    });
  }
  return (
    <>
      <meta charSet="utf-8" name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      {metaData.searchTags && <meta name="keywords" content={metaData.searchTags} />}
      <script type="text/javascript">{CommonUtils.default.getBrowserPolyfills()}</script>
      {metaData.siteName && metaData.title ? <title>{`${metaData.siteName} | ${metaData.title}`}</title> : null}
      {metaData.siteName && !metaData.title ? <title>{`${metaData.siteName}`}</title> : null}
      {metaTags.map((tag: any) => (
        <meta key={tag.name || tag.property} {...tag} />
      ))}
      {metaLinks.map((tag: any) => (
        <link key={tag.rel} {...tag} />
      ))}
      <meta name="page-updated" data-type="string" content={metaData.updatedAt} />
      <meta name="page-lang" data-type="string" content={metaData.pageLocale} />
      <meta name="page-type" data-type="string" content={metaData.pageType} />
      {metaData.siteName === 'Osmo' && (
        <meta name="facebook-domain-verification" content="rrmlbq6kbgvok3uzhzvs1t4tikgnx6" />
      )}
      {image && <meta name="preview-image" data-type="string" content={JSON.stringify(image)} />}
      {metaData.swiftTypeDate && <meta name="date" data-type="string" content={metaData.swiftTypeDate} />}
      {metaData.swiftTypeLocation && <meta name="location" data-type="string" content={metaData.swiftTypeLocation} />}
      {!!config.googleSiteVerification && (
        <meta name="google-site-verification" content={config.googleSiteVerification} />
      )}
    </>
  );
};

export default BaseHead;
